import React, { useState, useMemo, useEffect } from 'react';
import { ResponsiveSankey } from '@nivo/sankey';
import { ModelDataResponse } from '../../types';

interface SensitivityAnalysisPageProps {
  modelData: ModelDataResponse;
}

// Hilfsfunktion: Extrahiere Features und Targets aus den Importances
function extractFeaturesAndTargets(importanceData: Record<string, Record<string, number>>) {
  const targets = Object.keys(importanceData);
  const featuresSet = new Set<string>();
  targets.forEach(t => {
    Object.keys(importanceData[t]).forEach(f => featuresSet.add(f));
  });
  return { features: Array.from(featuresSet), targets };
}

export const SensitivityAnalysisPage: React.FC<SensitivityAnalysisPageProps> = ({ modelData }) => {
  // Importances aus ModelData extrahieren
  const aggData = modelData.model_metrics.permutation_importance_aggregated_sum1 as Record<string, Record<string, number>>;
  const rawData = modelData.model_metrics.permutation_importance_raw_sum1 as Record<string, Record<string, number>>;
  const [useRaw, setUseRaw] = useState(false);
  const importanceData = useRaw ? rawData : aggData;

  // Features und Targets extrahieren
  const { features, targets } = useMemo(() => extractFeaturesAndTargets(importanceData), [importanceData]);
  
  // State für ausgewählte Features/Targets
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>(features.slice(0, 3));
  const [selectedTargets, setSelectedTargets] = useState<string[]>(targets.slice(0, 1));
  
  // State für aufklappbare Menüs
  const [showFeatureSelector, setShowFeatureSelector] = useState(false);
  const [showTargetSelector, setShowTargetSelector] = useState(false);
  const [showDiagramSettings, setShowDiagramSettings] = useState(false);

  // --- Initialauswahl nach Daten-Update setzen ---
  useEffect(() => {
    setSelectedFeatures(features.slice(0, 3));
    setSelectedTargets(targets.slice(0, 1));
  }, [features, targets]);

  // --- Diagramm-Einstellungen State ---
  const [layout, setLayout] = useState<'horizontal' | 'vertical'>('horizontal');
  const [motionConfig, setMotionConfig] = useState<'default' | 'wobbly' | 'gentle' | 'stiff'>('default');
  const [colorScheme, setColorScheme] = useState<'category10' | 'pastel2' | 'blues' | 'greys' | 'dark2' | 'paired'>('category10');
  const [sort, setSort] = useState<'input' | 'descending' | 'ascending'>('input');
  const [linkOpacityBase, setLinkOpacityBase] = useState<number>(0.8);
  const [nodeThickness, setNodeThickness] = useState<number>(18);
  const [nodeSpacing, setNodeSpacing] = useState<number>(24);

  // --- Funktion für zufällige Einstellungen ---
  const randomizeSettings = () => {
    const layouts: ('horizontal' | 'vertical')[] = ['horizontal', 'vertical'];
    const motions: ('default' | 'wobbly' | 'gentle' | 'stiff')[] = ['default', 'wobbly', 'gentle', 'stiff'];
    const colors: ('category10' | 'pastel2' | 'blues' | 'greys' | 'dark2' | 'paired')[] = ['category10', 'pastel2', 'blues', 'greys', 'dark2', 'paired'];
    const sorts: ('input' | 'descending' | 'ascending')[] = ['input', 'descending', 'ascending'];
    const opacities = [0.4, 0.6, 0.8, 1.0];
    const thicknesses = [12, 15, 18, 21, 24];
    const spacings = [16, 20, 24, 28, 32];

    setLayout(layouts[Math.floor(Math.random() * layouts.length)]);
    setMotionConfig(motions[Math.floor(Math.random() * motions.length)]);
    setColorScheme(colors[Math.floor(Math.random() * colors.length)]);
    setSort(sorts[Math.floor(Math.random() * sorts.length)]);
    setLinkOpacityBase(opacities[Math.floor(Math.random() * opacities.length)]);
    setNodeThickness(thicknesses[Math.floor(Math.random() * thicknesses.length)]);
    setNodeSpacing(spacings[Math.floor(Math.random() * spacings.length)]);
  };

  // --- Feature-Importances für Sortierung und Opazität berechnen ---
  const featureImportances = useMemo(() => {
    const importances: Record<string, number> = {};
    features.forEach(f => {
      let sum = 0;
      selectedTargets.forEach(t => {
        if (importanceData[t] && importanceData[t][f]) {
          sum += importanceData[t][f];
        }
      });
      importances[f] = sum;
    });
    return importances;
  }, [features, importanceData, selectedTargets]);

  // --- Features für die Auswahl sortieren (descending) ---
  const sortedFeatures = useMemo(() => {
    return [...features].sort((a, b) => (featureImportances[b] ?? 0) - (featureImportances[a] ?? 0));
  }, [features, featureImportances]);

  // --- Funktionen für Voreinstellungen ---
  const selectTopFeatures = (count: number) => {
    setSelectedFeatures(sortedFeatures.slice(0, count));
  };

  const selectAllFeatures = () => {
    setSelectedFeatures([...features]);
  };

  const clearFeatures = () => {
    setSelectedFeatures([]);
  };

  const selectAllTargets = () => {
    setSelectedTargets([...targets]);
  };

  const clearTargets = () => {
    setSelectedTargets([]);
  };

  // --- Prozentwerte für Node-Labels und Target-Labels ---
  const nivoSankeyData = useMemo(() => {
    const featurePercents: Record<string, string> = {};
    selectedFeatures.forEach(f => {
      let sum = 0;
      selectedTargets.forEach(t => {
        if (importanceData[t] && importanceData[t][f]) {
          sum += importanceData[t][f];
        }
      });
      const percent = sum * 100;
      featurePercents[f] = percent < 1 ? '<1%' : `${Math.round(percent)}%`;
    });
    const targetPercents: Record<string, string> = {};
    selectedTargets.forEach(t => {
      let sum = 0;
      if (importanceData[t]) {
        Object.entries(importanceData[t]).forEach(([f, value]) => {
          if (selectedFeatures.includes(f)) {
            sum += value;
          }
        });
      }
      const percent = sum * 100;
      targetPercents[t] = percent < 1 ? '<1%' : `${Math.round(percent)}%`;
    });
    const nodes = [
      ...selectedFeatures.map(name => ({ id: `${name} (${featurePercents[name]})` })),
      ...selectedTargets.map(name => ({ id: `${name} (${targetPercents[name]})` })),
    ];
    // Dynamische Link-Farbe (RGBA) je nach Importance
    // Skaliere Importance linear auf Opazität zwischen 0.3 und 1.0
    let min = Infinity, max = -Infinity;
    selectedTargets.forEach(t => {
      if (importanceData[t]) {
        Object.entries(importanceData[t]).forEach(([f, value]) => {
          if (selectedFeatures.includes(f) && value > 0) {
            if (value < min) min = value;
            if (value > max) max = value;
          }
        });
      }
    });
    const links: { source: string; target: string; value: number; color: string }[] = [];
    selectedTargets.forEach(t => {
      if (importanceData[t]) {
        Object.entries(importanceData[t]).forEach(([f, value]) => {
          if (selectedFeatures.includes(f) && value > 0) {
            let norm = 1;
            if (max > min) {
              norm = (value - min) / (max - min);
            }
            let color = '';
            const alpha = linkOpacityBase;
            if (colorScheme === 'greys') {
              // Von hellgrau (220) zu dunkelgrau (60)
              const grey = Math.round(220 - 160 * norm);
              color = `rgba(${grey},${grey},${grey},${alpha})`;
            } else if (colorScheme === 'blues') {
              // Von hellblau (180,200,240) zu dunkelblau (31,119,180)
              const r = Math.round(180 - 149 * norm);
              const g = Math.round(200 - 81 * norm);
              const b = Math.round(240 - 60 * norm);
              color = `rgba(${r},${g},${b},${alpha})`;
            } else {
              // Default: rot
              const r = Math.round(227 - 100 * norm);
              const g = Math.round(26 + 100 * norm);
              const b = Math.round(28 + 100 * norm);
              color = `rgba(${r},${g},${b},${alpha})`;
            }
            links.push({ source: `${f} (${featurePercents[f]})`, target: `${t} (${targetPercents[t]})`, value, color });
          }
        });
      }
    });
    return { nodes, links };
  }, [importanceData, selectedFeatures, selectedTargets, colorScheme, linkOpacityBase]);

  // --- Darkmode erkennen ---
  const [isDark, setIsDark] = useState(false);
  useEffect(() => {
    const checkDark = () => {
      setIsDark(document.body.classList.contains('dark-theme'));
    };
    checkDark();
    window.addEventListener('storage', checkDark);
    return () => window.removeEventListener('storage', checkDark);
  }, []);

  return (
    <>
      <div style={{ margin: '32px auto', padding: 16, maxWidth: '1400px' }}>
        {/* Haupttitel */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <h1 style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: 16 }}>
            Sensitivitätsanalyse
          </h1>
        </div>

        {/* Sankey-Diagramm - jetzt breiter und zentraler */}
        <div className="card" style={{ 
          width: '100%', 
          maxWidth: '1200px', 
          margin: '0 auto',
          height: 600, 
          borderRadius: 6, 
          boxSizing: 'border-box', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}>
          {nivoSankeyData.links.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#888', padding: 40 }}>
              Keine Daten für die aktuelle Auswahl.
            </div>
          ) : (
            <ResponsiveSankey
              data={nivoSankeyData}
              margin={{ top: 40, right: 160, bottom: 40, left: 50 }}
              align="justify"
              colors={{ scheme: colorScheme }}
              nodeOpacity={1}
              nodeHoverOthersOpacity={0.35}
              nodeThickness={nodeThickness}
              nodeSpacing={nodeSpacing}
              linkOpacity={linkOpacityBase}
              linkHoverOthersOpacity={0.1}
              enableLinkGradient={true}
              labelPosition={layout === 'horizontal' ? 'inside' : 'outside'}
              labelOrientation={layout}
              labelPadding={16}
              labelTextColor={{ from: 'color', modifiers: [['darker', isDark ? 0.2 : 1]] }}
              theme={{
                tooltip: {
                  container: {
                    background: isDark ? '#23272e' : '#fff',
                    color: isDark ? '#e0e0e0' : '#333',
                    fontSize: 14,
                    border: `1px solid ${isDark ? '#444' : '#ccc'}`,
                    borderRadius: 4,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  },
                },
                labels: {
                  text: {
                    fontSize: 18,
                    fill: isDark ? '#e0e0e0' : '#222',
                  }
                }
              }}
              layout={layout}
              motionConfig={motionConfig}
              sort={sort}
            />
          )}
        </div>

        {/* Feature-Auswahl - Aufklappbares Menü */}
        <div className="card" style={{ 
          marginTop: 24, 
          padding: 16, 
          borderRadius: 6,
          maxWidth: '1200px',
          margin: '24px auto 0'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: showFeatureSelector ? 16 : 0,
            cursor: 'pointer'
          }} onClick={() => setShowFeatureSelector(!showFeatureSelector)}>
            <h3 style={{ margin: 0, fontWeight: 600 }}>
              Feature-Auswahl ({selectedFeatures.length} ausgewählt)
            </h3>
            <span style={{ fontSize: '1.2rem' }}>
              {showFeatureSelector ? '▼' : '▶'}
            </span>
          </div>
          
          {showFeatureSelector && (
            <div>
              {/* Raw/Aggregiert Toggle */}
              <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #eee' }}>
                <label style={{ fontWeight: 500, display: 'flex', alignItems: 'center' }}>
                  <span style={{ marginRight: 8 }}>Datenquelle:</span>
                  <input
                    type="checkbox"
                    checked={useRaw}
                    onChange={e => setUseRaw(e.target.checked)}
                    style={{ marginRight: 6 }}
                    id="toggle-raw"
                  />
                  <label htmlFor="toggle-raw" style={{ cursor: 'pointer' }}>
                    {useRaw ? 'Raw Importances' : 'Aggregierte Importances'}
                  </label>
                </label>
              </div>

              {/* Voreinstellungs-Buttons */}
              <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button 
                  type="button" 
                  onClick={() => selectTopFeatures(3)}
                  style={{ 
                    padding: '6px 12px', 
                    borderRadius: 4, 
                    border: '1px solid #ccc',
                    backgroundColor: selectedFeatures.length === 3 && JSON.stringify(selectedFeatures.sort()) === JSON.stringify(sortedFeatures.slice(0, 3).sort()) ? '#007bff' : 'transparent',
                    color: selectedFeatures.length === 3 && JSON.stringify(selectedFeatures.sort()) === JSON.stringify(sortedFeatures.slice(0, 3).sort()) ? 'white' : 'inherit'
                  }}
                >
                  Top 3
                </button>
                <button 
                  type="button" 
                  onClick={() => selectTopFeatures(5)}
                  style={{ 
                    padding: '6px 12px', 
                    borderRadius: 4, 
                    border: '1px solid #ccc',
                    backgroundColor: selectedFeatures.length === 5 && JSON.stringify(selectedFeatures.sort()) === JSON.stringify(sortedFeatures.slice(0, 5).sort()) ? '#007bff' : 'transparent',
                    color: selectedFeatures.length === 5 && JSON.stringify(selectedFeatures.sort()) === JSON.stringify(sortedFeatures.slice(0, 5).sort()) ? 'white' : 'inherit'
                  }}
                >
                  Top 5
                </button>
                <button 
                  type="button" 
                  onClick={selectAllFeatures}
                  style={{ 
                    padding: '6px 12px', 
                    borderRadius: 4, 
                    border: '1px solid #ccc',
                    backgroundColor: selectedFeatures.length === features.length ? '#007bff' : 'transparent',
                    color: selectedFeatures.length === features.length ? 'white' : 'inherit'
                  }}
                >
                  Alle
                </button>
                <button 
                  type="button" 
                  onClick={clearFeatures}
                  style={{ padding: '6px 12px', borderRadius: 4, border: '1px solid #ccc' }}
                >
                  Keine
                </button>
              </div>
              
              {/* Checkbox-Liste für Features */}
              <div style={{ 
                maxHeight: 200, 
                overflowY: 'auto', 
                border: '1px solid #ddd', 
                borderRadius: 4, 
                padding: 12,
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: 8
              }}>
                {sortedFeatures.map(f => (
                  <div key={f} style={{ display: 'flex', alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      id={`feature-checkbox-${f}`}
                      checked={selectedFeatures.includes(f)}
                      onChange={e => {
                        if (e.target.checked) {
                          setSelectedFeatures(prev => [...prev, f]);
                        } else {
                          setSelectedFeatures(prev => prev.filter(x => x !== f));
                        }
                      }}
                    />
                    <label 
                      htmlFor={`feature-checkbox-${f}`} 
                      style={{ 
                        marginLeft: 6, 
                        fontSize: '0.9rem',
                        cursor: 'pointer'
                      }}
                    >
                      {f}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Target-Auswahl - Aufklappbares Menü */}
        <div className="card" style={{ 
          marginTop: 16, 
          padding: 16, 
          borderRadius: 6,
          maxWidth: '1200px',
          margin: '16px auto 0'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: showTargetSelector ? 16 : 0,
            cursor: 'pointer'
          }} onClick={() => setShowTargetSelector(!showTargetSelector)}>
            <h3 style={{ margin: 0, fontWeight: 600 }}>
              Target-Auswahl ({selectedTargets.length} ausgewählt)
            </h3>
            <span style={{ fontSize: '1.2rem' }}>
              {showTargetSelector ? '▼' : '▶'}
            </span>
          </div>
          
          {showTargetSelector && (
            <div>
              {/* Voreinstellungs-Buttons */}
              <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button 
                  type="button" 
                  onClick={selectAllTargets}
                  style={{ 
                    padding: '6px 12px', 
                    borderRadius: 4, 
                    border: '1px solid #ccc',
                    backgroundColor: selectedTargets.length === targets.length ? '#007bff' : 'transparent',
                    color: selectedTargets.length === targets.length ? 'white' : 'inherit'
                  }}
                >
                  Alle
                </button>
                <button 
                  type="button" 
                  onClick={clearTargets}
                  style={{ padding: '6px 12px', borderRadius: 4, border: '1px solid #ccc' }}
                >
                  Keine
                </button>
              </div>
              
              {/* Checkbox-Liste für Targets */}
              <div style={{ 
                maxHeight: 150, 
                overflowY: 'auto', 
                border: '1px solid #ddd', 
                borderRadius: 4, 
                padding: 12,
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: 8
              }}>
                {targets.sort().map(t => (
                  <div key={t} style={{ display: 'flex', alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      id={`target-checkbox-${t}`}
                      checked={selectedTargets.includes(t)}
                      onChange={e => {
                        if (e.target.checked) {
                          setSelectedTargets(prev => [...prev, t]);
                        } else {
                          setSelectedTargets(prev => prev.filter(x => x !== t));
                        }
                      }}
                    />
                    <label 
                      htmlFor={`target-checkbox-${t}`} 
                      style={{ 
                        marginLeft: 6, 
                        fontSize: '0.9rem',
                        cursor: 'pointer'
                      }}
                    >
                      {t}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Diagramm-Einstellungen - jetzt aufklappbar und unter der Target-Auswahl */}
        <div className="card" style={{ 
          marginTop: 16, 
          padding: 16, 
          borderRadius: 6,
          maxWidth: '1200px',
          margin: '16px auto 0'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: showDiagramSettings ? 16 : 0,
            cursor: 'pointer'
          }} onClick={() => setShowDiagramSettings(!showDiagramSettings)}>
            <h3 style={{ margin: 0, fontWeight: 600 }}>
              Diagramm-Einstellungen
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button 
                type="button" 
                onClick={(e) => {
                  e.stopPropagation();
                  randomizeSettings();
                }}
                style={{ 
                  padding: '6px 12px', 
                  borderRadius: 4, 
                  border: '1px solid #28a745',
                  backgroundColor: '#28a745',
                  color: 'white',
                  fontSize: '0.9rem',
                  cursor: 'pointer'
                }}
                title="Zufällige Diagramm-Einstellungen anwenden"
              >
                🎲 Überraschen
              </button>
              <span style={{ fontSize: '1.2rem' }}>
                {showDiagramSettings ? '▼' : '▶'}
              </span>
            </div>
          </div>
          
          {showDiagramSettings && (
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center' }}>
              {/* Layout */}
              <label>
                Ausrichtung:
                <select value={layout} onChange={e => setLayout(e.target.value as 'horizontal' | 'vertical')} style={{ marginLeft: 6 }}>
                  <option value="horizontal">Horizontal</option>
                  <option value="vertical">Vertikal</option>
                </select>
              </label>
              {/* Animation */}
              <label>
                Animation:
                <select value={motionConfig} onChange={e => setMotionConfig(e.target.value as 'default' | 'wobbly' | 'gentle' | 'stiff')} style={{ marginLeft: 6 }}>
                  <option value="default">Standard</option>
                  <option value="wobbly">Wobbly</option>
                  <option value="gentle">Sanft</option>
                  <option value="stiff">Steif</option>
                </select>
              </label>
              {/* Farbschema */}
              <label>
                Farbschema:
                <select value={colorScheme} onChange={e => setColorScheme(e.target.value as 'category10' | 'pastel2' | 'blues' | 'greys' | 'dark2' | 'paired')} style={{ marginLeft: 6 }}>
                  <option value="category10">Category10</option>
                  <option value="pastel2">Pastel2</option>
                  <option value="blues">Blues</option>
                  <option value="greys">Greys</option>
                  <option value="dark2">Dark2</option>
                  <option value="paired">Paired</option>
                </select>
              </label>
              {/* Link-Opazität */}
              <label>
                Link-Opazität:
                <select value={linkOpacityBase} onChange={e => setLinkOpacityBase(Number(e.target.value))} style={{ marginLeft: 6 }}>
                  <option value={0.4}>0.4</option>
                  <option value={0.6}>0.6</option>
                  <option value={0.8}>0.8</option>
                  <option value={1.0}>1.0</option>
                </select>
              </label>
              {/* Sortierung */}
              <label>
                Sortierung:
                <select value={sort} onChange={e => setSort(e.target.value as 'input' | 'descending' | 'ascending')} style={{ marginLeft: 6 }}>
                  <option value="input">Keine</option>
                  <option value="descending">Absteigend</option>
                  <option value="ascending">Aufsteigend</option>
                </select>
              </label>
              {/* Node Thickness */}
              <label>
                Node-Dicke:
                <select value={nodeThickness} onChange={e => setNodeThickness(Number(e.target.value))} style={{ marginLeft: 6 }}>
                  <option value={12}>Klein (12)</option>
                  <option value={15}>Mittel-Klein (15)</option>
                  <option value={18}>Standard (18)</option>
                  <option value={21}>Mittel-Groß (21)</option>
                  <option value={24}>Groß (24)</option>
                </select>
              </label>
              {/* Node Spacing */}
              <label>
                Node-Abstand:
                <select value={nodeSpacing} onChange={e => setNodeSpacing(Number(e.target.value))} style={{ marginLeft: 6 }}>
                  <option value={16}>Eng (16)</option>
                  <option value={20}>Mittel-Eng (20)</option>
                  <option value={24}>Standard (24)</option>
                  <option value={28}>Mittel-Weit (28)</option>
                  <option value={32}>Weit (32)</option>
                </select>
              </label>
            </div>
          )}
        </div>

        {/* Debug-Tabelle unterhalb der Selektoren */}
        <div className="debug-box" style={{ 
          marginTop: 24, 
          borderRadius: 6, 
          padding: 12, 
          width: '100%', 
          overflowX: 'auto',
          maxWidth: '1200px',
          margin: '24px auto 0'
        }}>
          <h4 style={{ marginBottom: 8 }}>Debug: Live-Daten für Sankey</h4>
          <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
            <div>
              <b>Selected Features:</b>
              <ul>{selectedFeatures.map(f => <li key={f}>{f}</li>)}</ul>
            </div>
            <div>
              <b>Selected Targets:</b>
              <ul>{selectedTargets.map(t => <li key={t}>{t}</li>)}</ul>
            </div>
            <div>
              <b>Nodes:</b>
              <ul>{nivoSankeyData.nodes.map((n, i) => <li key={i}>{i}: {n.id}</li>)}</ul>
            </div>
            <div>
              <b>Links:</b>
              <ul>{nivoSankeyData.links.map((l, i) => <li key={i}>source: {l.source}, target: {l.target}, value: {l.value}</li>)}</ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}; 